import { parseInventoryQuantity } from '../utils/inventoryInputs';
import { cleanManufacturerUrl } from '../utils/manufacturerUrl';
import axios from 'axios';
import { hasProductPackFields, productPackPayload, validateProductPack } from '../utils/productPack';
import { parseMoneyAmount } from '../utils/productPrice';
import { stockQuantitiesChanged } from '../utils/stockQuantities';

const API_BASE_URL = '/api/v1/admin';

const authHeaders = () => ({
  Authorization: localStorage.getItem('token'),
});

const trimOrNull = value => {
  if (value == null) return null;
  const s = String(value).trim();
  return s === '' ? null : s;
};

const toMoneyOrNull = value => parseMoneyAmount(value);

const toUahPrice = value => {
  const n = parseMoneyAmount(value);
  if (n == null) return 0;
  return Math.max(0, Math.round(n));
};

const toQuantity = value => {
  const quantity = parseInventoryQuantity(value === '' || value == null ? 0 : value, { allowZero: true });
  if (quantity == null) throw new Error('Кількість має бути цілим невід’ємним числом у допустимих межах');
  return quantity;
};

export const mapBulkCreateProductItem = it => ({
  productCode: trimOrNull(it.productCode),
  barcode: trimOrNull(it.barcode),
  name: it.name != null ? String(it.name).trim() : '',
  description: it.description != null ? String(it.description) : '',
  manufacturerUrl: trimOrNull(cleanManufacturerUrl(it.manufacturerUrl)),
  price: toUahPrice(it.price),
  quantity: toQuantity(it.quantity),
  priceInPounds: toMoneyOrNull(it.priceInPounds),
  priceInUSD: toMoneyOrNull(it.priceInUSD),
  priceInEuro: toMoneyOrNull(it.priceInEuro),
  state: it.state || 'ACTIVE',
  categoryIds: it.categoryIds || [],
  discount: it.discount != null && it.discount !== '' ? Number(it.discount) : 0,
  discountCategoryName: it.discountCategoryName || null,
  expectedArrivalDate:
    it.expectedArrivalDate && String(it.expectedArrivalDate).trim() !== ''
      ? String(it.expectedArrivalDate).split('T')[0]
      : null,
});

// List and select-all must send the same filters; only the list needs paging and sorting.
const buildProductQuery = (params, paginated = false) => {
  const query = new URLSearchParams();
  const fields = [
    'productId',
    'search',
    'quantityFrom',
    'quantityTo',
    'priceFrom',
    'priceTo',
    'discountFrom',
    'discountTo',
    ...(paginated ? ['page', 'numberPerPage', 'sortBy', 'sortOrder'] : []),
  ];
  fields.forEach(key => {
    if (params[key] != null && params[key] !== '') query.append(key, params[key]);
  });
  ['state', 'categoryIds', 'discountCategoryNames'].forEach(key => {
    (params[key] || []).forEach(value => query.append(key, value));
  });
  if (params.barcodePresent === true || params.barcodePresent === false)
    query.append('barcodePresent', params.barcodePresent);
  if (params.includeEmptyDiscountCategory === true)
    query.append('includeEmptyDiscountCategory', 'true');
  const search = query.toString();
  return search ? `?${search}` : '';
};

export const adminProductApi = {
  uploadProductImages: (productId, data) =>
    axios.put(`/api/v1/product/${productId}/image`, data, { headers: authHeaders() }),

  deleteProductImage: (productId, imageName) =>
    axios.delete(`/api/v1/product/${productId}/image`, {
      params: { imageName },
      headers: authHeaders(),
    }),

  parseProductImages: (productId, url) =>
    axios.get(`/api/v1/product/${productId}/image/parse`, {
      params: { url },
      headers: authHeaders(),
    }),

  /** Admin helper: lightweight search used in incoming flows (bind product). */
  searchProductsInfo: searchParam =>
    axios.get(`/api/v1/product/search-info?searchParam=${encodeURIComponent(searchParam)}`, {
      headers: authHeaders(),
    }),
  getProducts: (params = {}) =>
    axios.get(`${API_BASE_URL}/products${buildProductQuery(params, true)}`, {
      headers: authHeaders(),
    }),

  getProductIds: (params = {}) =>
    axios.get(`${API_BASE_URL}/products/ids${buildProductQuery(params)}`, {
      headers: authHeaders(),
    }),

  getInnerProducts: parentId => {
    return axios.get(`${API_BASE_URL}/products/inner/${parentId}`, {
      headers: authHeaders(),
    });
  },

  getStockHistory: productId =>
    axios.get(`${API_BASE_URL}/products/${productId}/stock-history`, {
      headers: authHeaders(),
    }),

  patchStockHistoryComment: (productId, historyId, comment) =>
    axios.patch(
      `${API_BASE_URL}/products/${productId}/stock-history/${historyId}`,
      { comment: comment == null ? null : String(comment) },
      { headers: authHeaders() },
    ),

  /** Надходження з адмін-таблиці товарів (JWT → user_id в stock_history). */
  addProductQuantity: (productId, quantity, purchasePrice) => {
    const q = encodeURIComponent(quantity);
    const p = encodeURIComponent(purchasePrice);
    return axios.put(
      `/api/v1/product/${productId}/add-quantity?quantity=${q}&purchasePrice=${p}`,
      {},
      { headers: authHeaders() },
    );
  },

  getCategories: () => {
    return axios.get(`${API_BASE_URL}/categories`, {
      headers: authHeaders(),
    });
  },

  getDiscountCategories: () => {
    return axios.get(`${API_BASE_URL}/discount-categories`, {
      headers: authHeaders(),
    });
  },

  createProduct: data => {
    const packError = validateProductPack(data);
    if (packError) return Promise.reject(new Error(packError));
    const expectedArrivalDate =
      data.expectedArrivalDate && String(data.expectedArrivalDate).trim() !== ''
        ? typeof data.expectedArrivalDate === 'string'
          ? data.expectedArrivalDate.split('T')[0]
          : data.expectedArrivalDate
        : null;
    const body = {
      productCode: data.productCode || null,
      barcode: data.barcode || null,
      ...productPackPayload(data),
      name: data.name,
      description: data.description || '',
      price: data.price != null ? Number(data.price) : 0,
      quantity: data.quantity != null ? Number(data.quantity) : 0,
      discount: data.discount != null ? Number(data.discount) : 0,
      parentId: data.parentId || null,
      priceInPounds:
        data.priceInPounds != null && data.priceInPounds !== '' ? Number(data.priceInPounds) : null,
      priceInUSD:
        data.priceInUSD != null && data.priceInUSD !== '' ? Number(data.priceInUSD) : null,
      priceInEuro:
        data.priceInEuro != null && data.priceInEuro !== '' ? Number(data.priceInEuro) : null,
      purchasePrice:
        data.purchasePrice != null && data.purchasePrice !== '' ? Number(data.purchasePrice) : null,
      expectedArrivalDate,
      manufacturerUrl: trimOrNull(cleanManufacturerUrl(data.manufacturerUrl)),
      state: data.state != null && data.state !== '' ? data.state : undefined,
      discountCategoryName:
        data.discountCategoryName != null && data.discountCategoryName !== ''
          ? data.discountCategoryName
          : null,
      categoryIds: data.categoryIds || [],
      images: (data.images || []).map(img => (typeof img === 'string' ? img : img.src)),
    };
    return axios.post(`${API_BASE_URL}/products`, body, {
      headers: authHeaders(),
    });
  },

  bulkCreateProducts: items => {
    const body = {
      items: (items || []).map(mapBulkCreateProductItem),
    };
    return axios.post(`${API_BASE_URL}/products/bulk-create`, body, {
      headers: authHeaders(),
    });
  },

  /** Штрихкод і кількість пачки зберігаються разом. */
  patchProductPack: (productId, data) => {
    const error = validateProductPack(data);
    if (error) return Promise.reject(new Error(error));
    return axios.patch(`${API_BASE_URL}/products/${productId}/pack`, productPackPayload(data), {
      headers: authHeaders(),
    });
  },

  /** Лише штрихкод (без зміни фото/категорій). barcode: null або '' — очистити. */
  patchProductBarcode: (productId, barcode) => {
    const body = { barcode: barcode == null || barcode === '' ? null : String(barcode).trim() };
    return axios.patch(`${API_BASE_URL}/products/${productId}/barcode`, body, {
      headers: authHeaders(),
    });
  },

  /** Лише артикул (product_code). Порожній рядок на бекенді відхиляється. */
  patchProductCode: (productId, code) => {
    const body = { code: code == null ? '' : String(code).trim() };
    return axios.patch(`${API_BASE_URL}/products/${productId}/code`, body, {
      headers: authHeaders(),
    });
  },

  updateProduct: data => {
    const packError = validateProductPack(data);
    if (packError) return Promise.reject(new Error(packError));
    const expectedArrivalDate =
      data.expectedArrivalDate && String(data.expectedArrivalDate).trim() !== ''
        ? typeof data.expectedArrivalDate === 'string'
          ? data.expectedArrivalDate.split('T')[0]
          : data.expectedArrivalDate
        : null;
    const body = {
      id: data.id,
      quantityChangeReason: trimOrNull(data.quantityChangeReason),
      productCode: data.productCode || null,
      ...(Object.prototype.hasOwnProperty.call(data, 'barcode') ? { barcode: data.barcode || null } : {}),
      ...(hasProductPackFields(data) ? productPackPayload(data) : {}),
      name: data.name,
      description: data.description || '',
      price: data.price != null ? Number(data.price) : 0,
      quantity: data.quantity != null && (data.originalQuantity == null || stockQuantitiesChanged(data))
        ? Number(data.quantity) : undefined,
      discount: data.discount != null ? Number(data.discount) : 0,
      parentId: data.parentId || null,
      priceInPounds:
        data.priceInPounds != null && data.priceInPounds !== '' ? Number(data.priceInPounds) : null,
      priceInUSD:
        data.priceInUSD != null && data.priceInUSD !== '' ? Number(data.priceInUSD) : null,
      priceInEuro:
        data.priceInEuro != null && data.priceInEuro !== '' ? Number(data.priceInEuro) : null,
      purchasePrice:
        data.purchasePrice != null && data.purchasePrice !== '' ? Number(data.purchasePrice) : null,
      expectedArrivalDate,
      manufacturerUrl: trimOrNull(cleanManufacturerUrl(data.manufacturerUrl)),
      state: data.state != null && data.state !== '' ? data.state : undefined,
      discountCategoryName:
        data.discountCategoryName != null ? data.discountCategoryName : undefined,
      categoryIds: data.categoryIds || [],
      images: (data.images || []).map(img => (typeof img === 'string' ? img : img.src)),
    };
    return axios.put(`${API_BASE_URL}/products`, body, {
      headers: authHeaders(),
    });
  },

  deleteProduct: productId => {
    return axios.put(
      `${API_BASE_URL}/products/delete/${productId}`,
      {},
      {
        headers: authHeaders(),
      },
    );
  },

  /**
   * Bulk update: productIds + optional expectedArrivalDate, clearExpectedArrivalDate, discount, etc.
   */
  bulkUpdateProducts: data => {
    const body = { productIds: data.productIds };
    if (data.clearExpectedArrivalDate === true) {
      body.clearExpectedArrivalDate = true;
    } else if (data.expectedArrivalDate != null && String(data.expectedArrivalDate).trim() !== '') {
      const d = data.expectedArrivalDate;
      body.expectedArrivalDate = typeof d === 'string' ? d.split('T')[0] : d;
    }
    if (data.discount != null) body.discount = Number(data.discount);
    if (data.categoryIds != null && Array.isArray(data.categoryIds))
      body.categoryIds = data.categoryIds;
    if (data.removeCategoryIds != null && Array.isArray(data.removeCategoryIds))
      body.removeCategoryIds = data.removeCategoryIds;
    if (data.state != null && data.state !== '') body.state = data.state;
    if (data.discountCategoryName != null) body.discountCategoryName = data.discountCategoryName;
    return axios.patch(`${API_BASE_URL}/products/bulk`, body, {
      headers: authHeaders(),
    });
  },
};
